/* eslint-disable no-unused-vars, jsx-a11y/alt-text, react-hooks/exhaustive-deps */
// Vendor Main Layout
import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api/axios';
import { toast } from 'react-hot-toast';
import {
    FiHome, FiCpu, FiGrid, FiMonitor, FiShoppingCart, FiMapPin, FiTv, FiList,
    FiUser, FiPackage, FiTrendingUp, FiAlertTriangle, FiPieChart, FiFileText,
    FiDollarSign, FiRotateCcw, FiTruck, FiBox, FiUsers, FiShield, FiBarChart2,
    FiActivity, FiGift, FiStar, FiSettings, FiRefreshCw,
    FiChevronDown, FiLogOut, FiMenu, FiSun, FiMoon, FiGlobe, FiCreditCard,
    FiDatabase, FiRepeat, FiHardDrive, FiLayout, FiBookOpen, FiTag,
    FiBook
} from 'react-icons/fi';
import { FaUtensils } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import useTawkTo from '../hooks/useTawkTo';
import NotificationCenter from '../components/NotificationCenter';
import OfflineIndicator from '../components/common/OfflineIndicator';

const Sidebar = ({ isOpen, setIsOpen }) => {
    const { user, logout, hasPermission } = useAuth();
    const { t, isRtl } = useLanguage();
    const { darkMode, themeMode } = useTheme();
    const location = useLocation();
    const [expandedMenus, setExpandedMenus] = useState({});
    const [logoUrl, setLogoUrl] = useState('/logo-brand.jpg');

    useEffect(() => {
        const savedLogo = localStorage.getItem('system_logo_url');
        if (savedLogo) setLogoUrl(savedLogo);
    }, []);

    // Memoize permissions check — يدعم كل صيغ الميزات
    const hasFeature = React.useCallback(
        (feature) => {
            if (user?.is_super_admin || user?.role === 'vendor' || user?.role === 'admin') return true;
            const r = user?.restaurant || user?.restaurant_staff;
            if (!r) return true;

            // 1. active_features (plan + extra_features + legacy) — الأعلى أولوية
            const active = r.active_features;
            if (Array.isArray(active) && active.length > 0) {
                return active.includes(feature);
            }

            // 2. extra_features (overrides يدوية)
            const extra = r.extra_features;
            if (Array.isArray(extra) && extra.includes(feature)) return true;

            // 3. features — يدعم array أو object {pos: true}
            const features = r.features;
            if (Array.isArray(features)) return features.includes(feature);
            if (features && typeof features === 'object') return !!features[feature];

            return true;
        },
        [user]
    );




    const hasRole = React.useCallback(
        (roles) => {
            if (!roles || roles.includes('all')) return true;
            const userRole = user?.role || 'user';
            return roles.includes(userRole);
        },
        [user]
    );

    const toggleMenu = (name) => {
        setExpandedMenus((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const navLinks = React.useMemo(
        () => [
            {
                name: t('dashboard'),
                icon: <FiHome />,
                to: '/vendor/dashboard',
                roles: ['all'],
                permission: 'dashboard.view'
            },
            {
                name: t('sidebar_aiConsultant'),
                icon: <FiCpu className="text-purple-500 animate-pulse" />,
                to: '/vendor/ai-consultant',
                roles: ['vendor', 'admin'],
                badge: 'PREMIUM',
                permission: 'ai.access'
            },

            // --- القسم التشغيلي (Operations) ---
            {
                name: t('sidebar_operations'),
                icon: <FiGrid />,
                roles: ['all'],
                permission: 'orders.view',
                children: [
                    {
                        name: t('pos'),
                        icon: <FiMonitor />,
                        to: '/vendor/pos',
                        feature: 'pos',
                        roles: ['vendor', 'admin', 'cashier', 'waiter'],
                        permission: 'orders.manage'
                    },
                    {
                        name: t('orders'),
                        to: '/vendor/orders',
                        icon: <FiShoppingCart />,
                        feature: 'sales_management',
                        roles: ['all'],
                        permission: 'orders.view'
                    },
                    {
                        name: t('tablesLayout'),
                        to: '/vendor/tables',
                        icon: <FiMapPin />,
                        feature: 'table_management',
                        roles: ['vendor', 'admin', 'waiter'],
                        permission: 'tables.manage'
                    },
                    {
                        name: t('kds'),
                        icon: <FiTv />,
                        to: '/vendor/kitchen',
                        feature: 'kds',
                        roles: ['vendor', 'admin', 'chef'],
                        permission: 'service.kds'
                    },
                    {
                        name: t('sidebar_queueManagement'),
                        to: '/vendor/queue',
                        icon: <FiList />,
                        roles: ['vendor', 'admin', 'waiter']
                    },
                    {
                        name: t('sidebar_waiterApp'),
                        to: '/vendor/waiter-app',
                        icon: <FiUser />,
                        roles: ['vendor', 'admin', 'waiter']
                    },
                    {
                        name: t('kioskManagement'),
                        to: '/vendor/kiosk-settings',
                        icon: <FiLayout />,
                        roles: ['vendor', 'admin']
                    },
                    {
                        name: t('floorPlanTitle'),
                        to: '/vendor/floor-plan',
                        icon: <FiMapPin />,
                        roles: ['vendor', 'admin']
                    }
                ]
            },

            // --- المخزون والقائمة (Inventory & Menu) ---
            {
                name: t('sidebar_inventoryMenu'),
                icon: <FiPackage />,
                roles: ['vendor', 'admin', 'chef'],
                permission: 'menu.manage',
                children: [
                    {
                        name: t('menu'),
                        icon: <FaUtensils />,
                        to: '/vendor/menu',
                        roles: ['vendor', 'admin', 'chef', 'waiter', 'cashier'],
                        permission: 'menu.manage'
                    },
                    {
                        name: t('inventory'),
                        to: '/vendor/inventory',
                        icon: <FiGrid />,
                        roles: ['vendor', 'admin', 'chef'],
                        permission: 'inventory.manage'
                    },
                    {
                        name: 'المستودعات والمخازن',
                        to: '/vendor/warehouses',
                        icon: <FiBox />,
                        roles: ['vendor', 'admin'],
                        permission: 'inventory.manage'
                    },
                    {
                        name: t('sidebar_purchasesGroup'),
                        icon: <FiShoppingCart />,
                        roles: ['vendor', 'admin'],
                        permission: 'purchases.manage',
                        children: [
                            {
                                name: t('overview'),
                                to: '/vendor/purchases',
                                icon: <FiPieChart />
                            },
                            {
                                name: t('sidebar_purchaseInvoices'),
                                to: '/vendor/purchases/invoices',
                                icon: <FiFileText />
                            },
                            {
                                name: t('paymentManagement'),
                                to: '/vendor/purchases/payments',
                                icon: <FiDollarSign />
                            },
                            {
                                name: t('sidebar_purchaseReturns'),
                                to: '/vendor/purchases/returns',
                                icon: <FiRotateCcw />
                            }
                        ]
                    },
                    {
                        name: t('suppliers'),
                        to: '/vendor/suppliers',
                        icon: <FiTruck />,
                        roles: ['vendor', 'admin'],
                        permission: 'purchases.manage'
                    },
                    {
                        name: t('sidebar_customers') || t('customers'),
                        to: '/vendor/customers',
                        icon: <FiUsers />,
                        roles: ['vendor', 'admin'],
                        permission: 'customers.manage'
                    },
                    {
                        name: t('sidebar_lowStockAlerts') || 'تنبيهات المخزون',
                        to: '/vendor/inventory/alerts',
                        icon: <FiAlertTriangle />,
                        roles: ['vendor', 'admin'],
                        permission: 'inventory.manage'
                    },
                    {
                        name: t('sidebar_recipeCosting') || 'تسعير الوصفات',
                        to: '/vendor/recipe-costing',
                        icon: <FiTag />,
                        roles: ['vendor', 'admin'],
                        permission: 'menu.manage'
                    }
                ]
            },

            {
                name: t('sidebar_governance'),
                icon: <FiPieChart />,
                roles: ['vendor', 'admin'],
                permission: 'finance.view',
                children: [
                    {
                        name: t('sidebar_taxDeclaration'),
                        to: '/vendor/accounting/tax-report',
                        icon: <FiFileText />,
                        permission: 'reports.tax'
                    },
                    {
                        name: t('accounting'),
                        icon: <FiPieChart />,
                        to: '/vendor/accounting',
                        feature: 'accounting',
                        permission: 'finance.view'
                    },
                    {
                        name: t('sidebar_openingBalanceEntry') || 'القيد الافتتاحي',
                        icon: <FiBook />,
                        to: '/vendor/accounting/opening-balance-entry',
                        permission: 'finance.view'
                    },
                    {
                        name: t('zatcaIntegration'),
                        icon: <FiShield />,
                        to: '/vendor/zatca-integration',
                        feature: 'zatca',
                        permission: 'reports.tax'
                    },
                    {
                        name: t('sidebar_reportsOverviewSales'),
                        to: '/vendor/reports/sales',
                        icon: <FiBarChart2 />,
                        feature: 'sales_reports',
                        permission: 'finance.view'
                    },
                    {
                        name: t('sidebar_purchaseReports') || 'تقارير المشتريات',
                        to: '/vendor/reports/purchases',
                        icon: <FiFileText />,
                        permission: 'finance.view'
                    },
                    {
                        name: t('sidebar_advancedReports') || 'التقارير المتقدمة',
                        to: '/vendor/reports/advanced',
                        icon: <FiPieChart />,
                        permission: 'finance.view'
                    },
                    {
                        name: t('sidebar_assets') || 'الأصول الثابتة',
                        to: '/vendor/assets',
                        icon: <FiHardDrive />,
                        permission: 'finance.view'
                    }
                ]
            },

            {
                name: t('sidebar_hqConsole'),
                icon: <FiHome />,
                roles: ['vendor', 'admin'],
                permission: 'hq.access',
                children: [
                    {
                        name: t('sidebar_branchManagement') || 'إدارة الفروع',
                        icon: <FiGrid />,
                        to: '/vendor/branch-hq',
                        permission: 'hq.branches'
                    },
                    {
                        name: t('sidebar_hqDashboard') || 'لوحة التحكم الموحدة',
                        icon: <FiHome />,
                        to: '/vendor/hq-dashboard',
                        permission: 'hq.access'
                    },
                    {
                        name: t('sidebar_hqAccountingCenter') || 'المركز المالي الموحد',
                        icon: <FiFileText />,
                        to: '/vendor/hq-accounting-center',
                        permission: 'hq.access'
                    }
                ]
            },

            {
                name: t('sidebar_compliance'),
                icon: <FiShield className="text-emerald-500" />,
                roles: ['vendor', 'admin'],
                feature: 'sfda_compliance',
                permission: 'sfda.compliance',
                children: [
                    { name: t('batchManagement'), to: '/vendor/batches', icon: <FiBox />, permission: 'sfda.food_safety' },
                    { name: t('haccpChecks'), to: '/vendor/haccp', icon: <FiActivity />, permission: 'sfda.food_safety' },
                    { name: t('sidebar_trainingRecords') || 'سجلات التدريب', to: '/vendor/training-records', icon: <FiBookOpen />, permission: 'sfda.food_safety' },
                    { name: t('sidebar_complianceAI') || 'الامتثال AI', to: '/vendor/compliance-ai', icon: <FiCpu className="text-emerald-500" />, permission: 'sfda.compliance' }
                ]
            },

            {
                name: t('sidebar_marketingGrowth'),
                icon: <FiTrendingUp />,
                roles: ['vendor', 'admin'],
                permission: 'marketing.campaigns',
                children: [
                    {
                        name: t('loyalty'),
                        icon: <FiGift />,
                        to: '/vendor/loyalty',
                        feature: 'loyalty',
                        permission: 'customers.manage'
                    },
                    {
                        name: t('marketing') || 'التسويق والإعلانات',
                        icon: <FiMonitor />,
                        to: '/vendor/marketing',
                        feature: 'marketing',
                        permission: 'marketing.campaigns'
                    },
                    {
                        name: t('sidebar_aiMenuOptimization') || 'تحسين القائمة AI',
                        icon: <FiCpu className="text-purple-500" />,
                        to: '/vendor/menu-optimization',
                        badge: 'AI',
                        roles: ['vendor', 'admin'],
                        permission: 'ai.access'
                    },
                    {
                        name: t('sidebar_reputation') || 'إدارة السمعة',
                        icon: <FiStar />,
                        to: '/vendor/reputation',
                        roles: ['vendor', 'admin'],
                        permission: 'marketing.campaigns'
                    }
                ]
            },

            {
                name: t('sidebar_technicalAdmin') || 'الإدارة الفنية',
                icon: <FiSettings />,
                roles: ['vendor', 'admin'],
                permission: 'settings.manage',
                children: [
                    {
                        name: t('staff') || 'إدارة الموظفين',
                        icon: <FiUsers />,
                        to: '/vendor/staff',
                        feature: 'staff_management',
                        permission: 'staff.manage'
                    },

                    {
                        name: 'الربط مع التطبيقات',
                        to: '/vendor/integrations',
                        icon: <FiRefreshCw />,
                        permission: 'api.integrations'
                    },
                    {
                        name: t('sidebar_backup') || 'النسخ الاحتياطي',
                        to: '/vendor/backup',
                        icon: <FiDatabase />,
                        permission: 'settings.manage'
                    },

                    {
                        name: t('sidebar_paymentSettings') || 'إعدادات الدفع',
                        to: '/vendor/payment-settings',
                        icon: <FiCreditCard />,
                        permission: 'settings.manage'
                    },
                    {
                        name: 'سجل العمليات والنشاطات',
                        to: '/vendor/activity-logs',
                        icon: <FiActivity />,
                        permission: 'settings.manage'
                    },
                    {
                        name: t('settings') || 'إعدادات المطعم',
                        icon: <FiSettings />,
                        to: '/vendor/settings',
                        permission: 'settings.manage'
                    }
                ]
            }
        ],
        [t]
    );

    useEffect(() => {
        const path = location.pathname;
        const newExpanded = {};
        const checkChildren = (items) => {
            items.forEach((item) => {
                if (item.children) {
                    const directMatch = item.children.some((child) => child.to && path.startsWith(child.to));
                    if (directMatch) newExpanded[item.name] = true;
                    checkChildren(item.children); // Check nested
                }
            });
        };
        checkChildren(navLinks);
        setExpandedMenus((prev) => ({ ...prev, ...newExpanded }));
    }, [location.pathname, navLinks]);

    const NavItem = React.memo(({ item, level = 0 }) => {
        if (item.feature && !hasFeature(item.feature)) return null;
        if (item.roles && !hasRole(item.roles)) return null;
        if (item.permission && !hasPermission(item.permission)) return null;

        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedMenus[item.name];
        const visibleChildren = hasChildren ? item.children.filter(c =>
            (!c.feature || hasFeature(c.feature)) &&
            (!c.roles || hasRole(c.roles)) &&
            (!c.permission || hasPermission(c.permission))
        ) : [];
        if (hasChildren && visibleChildren.length === 0) return null;

        if (hasChildren) {
            return (
                <div className="mb-1">
                    <button
                        onClick={() => toggleMenu(item.name)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-bold rounded-xl transition-all ${isExpanded ? 'bg-slate-100 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
                    >
                        <div className="flex items-center">
                            <span className="text-xl opacity-80">{item.icon}</span>
                            <span className={isRtl ? 'mr-3' : 'ml-3'}>{item.name}</span>
                        </div>
                        <FiChevronDown className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className={`overflow-hidden mt-1 space-y-1 ${isRtl ? 'mr-4 border-r-2' : 'ml-4 border-l-2'} border-slate-100 dark:border-slate-800`}
                            >
                                {visibleChildren.map((child) => (
                                    <NavItem key={child.name} item={child} level={level + 1} />
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            );
        }

        return (
            <NavLink
                to={item.to}
                className={({ isActive }) =>
                    `flex items-center px-4 py-3 text-sm font-bold rounded-xl transition-all ${isActive ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200 dark:shadow-none' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`
                }
            >
                <span className="text-xl">{item.icon}</span>
                <span className={isRtl ? 'mr-3' : 'ml-3'}>{item.name}</span>
                {item.badge && (
                    <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter ${isRtl ? 'mr-auto' : 'ml-auto'} ${item.badge === 'AI' ? 'bg-purple-500 text-white' : 'bg-amber-400 text-white'}`}>
                        {item.badge}
                    </span>
                )}
            </NavLink>
        );
    });

    return (
        <aside
            className={`fixed inset-y-0 ${isRtl ? 'right-0 border-l' : 'left-0 border-r'} z-40 w-72 bg-white dark:bg-slate-900 shadow-2xl border-slate-200 dark:border-slate-800 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : isRtl ? 'translate-x-[101%]' : '-translate-x-[101%]'} ${location.pathname === '/vendor/pos' ? '' : 'lg:translate-x-0 lg:static lg:inset-0'} print:hidden`}
        >
            <div className="flex flex-col h-full">
                <div className={`flex items-center px-8 h-20 border-b ${darkMode ? 'border-slate-800' : 'border-slate-100'} ${themeMode === 'classic' ? 'bg-[#f8fafc]/50' : ''}`}>
                    <Link to="/vendor/dashboard" className="flex items-center gap-3">
                        <img 
                            src={logoUrl} 
                            alt="Logo" 
                            className="w-10 h-10 rounded-xl object-cover shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 hover:rotate-3 hover:brightness-110 hover:shadow-emerald-500/20 hover:shadow-xl cursor-pointer" 
                        />
                        <div>
                            <span className={`text-lg font-black tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>{t('appName') || 'DFOOD'}</span>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">Vendor Suite</p>
                        </div>
                    </Link>
                </div>
                <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto custom-scrollbar">
                    {navLinks.map((link) => (
                        <NavItem key={link.name} item={link} />
                    ))}
                </nav>
                <div className={`p-4 border-t ${darkMode ? 'bg-slate-800/50 border-slate-800' : 'bg-slate-50 border-slate-100'} ${themeMode === 'classic' ? 'bg-white' : ''}`}>
                    <div className={`flex items-center gap-3 p-3 rounded-2xl border ${darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'} ${themeMode === 'classic' ? 'border-slate-200 shadow-sm' : ''}`}>
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold shadow-inner ${themeMode === 'classic' ? 'bg-[#e8f8f5] text-[#1abc9c]' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'}`}>
                            {user?.name?.charAt(0)}
                        </div>
                        <div className="overflow-hidden">
                            <p className={`text-sm font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>{user?.name}</p>
                            <p className="text-[10px] text-slate-400 truncate font-medium">{user?.restaurant?.name || user?.restaurant_staff?.name || user?.restaurant_name || 'مطعمي'}</p>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        className="w-full flex items-center justify-center px-4 py-3 text-xs font-black rounded-xl text-rose-600 bg-rose-50 dark:bg-rose-500/10 hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-all border border-rose-100 dark:border-rose-500/20 group mt-3"
                    >
                        <FiLogOut className={`${isRtl ? 'ml-2 group-hover:-translate-x-1' : 'mr-2 group-hover:translate-x-1'} transition-transform`} />
                        {t('logout') || 'تسجيل الخروج'}
                    </button>
                </div>
            </div>
        </aside>
    );
};

const Header = ({ setSidebarOpen, fetchDashboardData, loading, user, logout, openChat, zoom, setZoom }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isRtl, language, toggleLanguage } = useLanguage();
    const { darkMode, toggleDarkMode, themeMode, toggleTheme } = useTheme();
    const [menuOpen, setMenuOpen] = useState(false);
    const [branchMenuOpen, setBranchMenuOpen] = useState(false);
    const [branches, setBranches] = useState([]);
    const [switching, setSwitching] = useState(false);

    // Fetch accessible branches (Odoo Pattern)
    useEffect(() => {
        const loadBranches = async () => {
            try {
                const response = await api.get('/vendor/hq/accessible-branches');
                setBranches(response.data.branches || []);
            } catch (error) {
                console.error("Failed to load branches");
            }
        };
        if (user) loadBranches();
    }, [user]);

    const handleSwitchBranch = async (branchId) => {
        try {
            setSwitching(true);
            await api.post('/vendor/hq/switch-context', { branch_id: branchId });
            toast.success(isRtl ? 'تم تبديل الفرع بنجاح، جاري التحويل...' : 'Context switched, redirecting...');
            setBranchMenuOpen(false);
            // Force reload to apply new context globally (TenantScope)
            window.location.href = '/vendor/dashboard';
        } catch (error) {
            toast.error(isRtl ? 'فشل التبديل' : 'Switch failed');
        } finally {
            setSwitching(false);
        }
    };

    const activeBranch = branches.find(b => b.id === (user?.current_branch_id || user?.restaurant_id)) || user?.restaurant || user?.restaurant_staff;

    return (
        <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-sm border-b border-slate-200 dark:border-slate-800 z-30 sticky top-0 print:hidden">
            <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className={`text-slate-500 dark:text-slate-400 p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors ${location.pathname === '/vendor/pos' ? '' : 'lg:hidden'}`}
                    >
                        <FiMenu size={24} />
                    </button>
                    <button
                        onClick={fetchDashboardData}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold rounded-xl border border-slate-100 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700 transition-all"
                    >
                        <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:block">{loading ? 'جاري التحديث...' : 'تحديث'}</span>
                    </button>

                    {/* Branch Switcher (Context Switcher) */}
                    {branches.length > 1 && (
                        <div className="relative ml-2">
                            <button
                                onClick={() => setBranchMenuOpen(!branchMenuOpen)}
                                className="flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 text-xs font-black rounded-xl border border-emerald-100 dark:border-emerald-900/50 hover:bg-emerald-100 transition-all"
                            >
                                <FiRepeat size={14} className={switching ? 'animate-spin' : ''} />
                                <span className="hidden sm:block truncate max-w-[120px]">
                                    {activeBranch?.name || 'اختر الفرع'}
                                </span>
                                <FiChevronDown size={14} className={`transition-transform ${branchMenuOpen ? 'rotate-180' : ''}`} />
                            </button>

                            <AnimatePresence>
                                {branchMenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className={`absolute mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-2 z-[60] ${isRtl ? 'right-0' : 'left-0'}`}
                                    >
                                        <div className="px-3 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 dark:border-slate-700 mb-1">
                                            {isRtl ? 'تبديل الفرع النشط' : 'Switch Context'}
                                        </div>
                                        <div className="max-h-60 overflow-y-auto custom-scrollbar">
                                            {branches.map(branch => (
                                                <button
                                                    key={branch.id}
                                                    onClick={() => handleSwitchBranch(branch.id)}
                                                    className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all mb-1 ${branch.id === activeBranch?.id ? 'bg-emerald-600 text-white' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${branch.id === activeBranch?.id ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-900'}`}>
                                                            {branch.name.charAt(0)}
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-xs font-bold truncate">{branch.name}</p>
                                                            <p className={`text-[9px] ${branch.id === activeBranch?.id ? 'text-white/70' : 'text-slate-400'}`}>
                                                                {branch.city || 'المركز الرئيسي'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {branch.id === activeBranch?.id && <div className="w-2 h-2 rounded-full bg-white shadow-lg shadow-white/50" />}
                                                </button>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                    {/* Zoom Control Widget */}
                    <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-2 py-1.5 rounded-xl">
                        <button
                            onClick={() => setZoom(prev => Math.max(70, prev - 5))}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-500 dark:text-slate-400 font-bold transition-all text-xs"
                            title={isRtl ? 'تصغير الشاشة' : 'Zoom Out'}
                        >
                            ➖
                        </button>
                        <span className="text-[10px] font-black text-slate-600 dark:text-slate-300 w-10 text-center select-none font-mono">
                            {zoom}%
                        </span>
                        <button
                            onClick={() => setZoom(prev => Math.min(130, prev + 5))}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg text-slate-500 dark:text-slate-400 font-bold transition-all text-xs"
                            title={isRtl ? 'تكبير الشاشة' : 'Zoom In'}
                        >
                            ➕
                        </button>
                    </div>

                    <button
                        onClick={toggleDarkMode}
                        className="p-3 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-xl transition-all"
                        title={darkMode ? 'الوضع الفاتح' : 'الوضع الليلي'}
                    >
                        {darkMode ? <FiSun size={20} /> : <FiMoon size={20} />}
                    </button>
                    <button
                        onClick={toggleTheme}
                        className={`p-3 rounded-xl transition-all ${themeMode === 'premium' ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' :
                            themeMode === 'luxury' ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' :
                                'text-slate-400 hover:text-slate-600'
                            }`}
                        title={
                            themeMode === 'premium' ? 'تبديل للثيم السابق' :
                                themeMode === 'luxury' ? 'تبديل للثيم الفاخر' :
                                    'تبديل للثيم الكلاسيكي'
                        }
                    >
                        🎨
                    </button>
                    <button
                        onClick={toggleLanguage}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-black rounded-xl border border-slate-100 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700 transition-all"
                    >
                        <FiGlobe /> {language === 'ar' ? 'English' : 'عربي'}
                    </button>
                    <button
                        onClick={openChat}
                        className="hidden sm:flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 text-xs font-black rounded-xl border border-indigo-100 dark:border-indigo-900/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-all animate-pulse"
                    >
                        <FiActivity /> {isRtl ? 'الدعم الفني' : 'Support'}
                    </button>
                    <NotificationCenter />
                    <div className="relative">
                        <button
                            onClick={() => setMenuOpen((prev) => !prev)}
                            className="flex items-center gap-3 p-1 pr-3 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-emerald-500/30 transition-all"
                        >
                            <div className="text-right">
                                <p className="text-xs font-black text-slate-900 dark:text-white leading-none mb-0.5 truncate max-w-[140px]">{user?.name}</p>
                                <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 truncate max-w-[140px] leading-tight">{user?.restaurant?.name || user?.restaurant_staff?.name || user?.restaurant_name || 'مطعمي'}</p>
                            </div>
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold shadow-lg shadow-emerald-900/20">
                                {user?.name?.charAt(0)}
                            </div>
                        </button>
                        <AnimatePresence>
                            {menuOpen && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                    className={`absolute mt-3 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-2 z-50 ${isRtl ? 'left-0 origin-top-left' : 'right-0 origin-top-right'}`}
                                >
                                    <button
                                        onClick={() => { setMenuOpen(false); navigate('/vendor/settings'); }}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-xl transition-all"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                            <FiUser size={16} />
                                        </div>
                                        الملف الشخصي
                                    </button>
                                    <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
                                    <button
                                        onClick={() => { setMenuOpen(false); logout(); }}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-all"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-rose-50 dark:bg-rose-500/10 text-rose-600 flex items-center justify-center">
                                            <FiLogOut size={16} />
                                        </div>
                                        تسجيل الخروج
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </header>
    );
};

const VendorLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user, logout, refreshProfile } = useAuth();
    const { isRtl } = useLanguage();
    const { darkMode, themeMode, setThemeMode } = useTheme();
    const location = useLocation();

    const [zoom, setZoom] = useState(() => {
        const saved = localStorage.getItem('vendor_zoom');
        return saved ? parseInt(saved, 10) : 100;
    });

    useEffect(() => {
        document.body.style.zoom = `${zoom}%`;
        localStorage.setItem('vendor_zoom', zoom.toString());
        return () => {
            document.body.style.zoom = '100%';
        };
    }, [zoom]);

    const zoomScale = zoom / 100;
    const computedMinHeight = `calc(100vh / ${zoomScale})`;

    // Default to Classic for Vendor Dashboard if no theme is saved
    useEffect(() => {
        if (!localStorage.getItem('themeMode')) {
            setThemeMode('classic');
        }
    }, [setThemeMode]);

    const { openChat } = useTawkTo({
        name: user?.name || 'Vendor',
        email: user?.email || '',
        attributes: {
            restaurantName: user?.restaurant?.name || '',
            role: user?.role || 'vendor',
            userId: String(user?.id || ''),
        },
        delay: 2,
        hide: true, // Hide default bubble
    });

    const fetchDashboardData = async () => {
        setLoading(true);
        try { await refreshProfile(); } catch (error) { console.error(error); } finally { setLoading(false); }
    };

    return (
        <div 
            className={`min-h-screen ${darkMode ? 'dark' : ''} ${themeMode === 'classic' ? 'theme-classic' : 'theme-premium'} flex font-cairo`} 
            style={{ minHeight: computedMinHeight }}
            dir={isRtl ? 'rtl' : 'ltr'}
        >
            {/* Dynamic Brand Identity Theme Injection */}
            <style>{`
                :root {
                    --brand-color: ${user?.restaurant?.brand_color || '#10b981'};
                    --brand-font: '${user?.restaurant?.brand_font || 'Cairo'}', sans-serif;
                }
                body, html, *, .font-cairo {
                    font-family: var(--brand-font) !important;
                }
                /* Sidebar, buttons, and accents */
                .theme-classic aside .sidebar-item-active,
                .theme-classic aside .bg-emerald-600.text-white,
                .bg-emerald-600,
                .bg-emerald-500 {
                    background-color: var(--brand-color) !important;
                }
                .text-emerald-600,
                .text-emerald-500,
                .theme-classic .text-emerald-600 {
                    color: var(--brand-color) !important;
                }
                .border-emerald-600,
                .border-emerald-500 {
                    border-color: var(--brand-color) !important;
                }
                .hover\:bg-emerald-700:hover,
                .hover\:bg-emerald-600:hover {
                    background-color: var(--brand-color) !important;
                    filter: brightness(0.9);
                }
                .bg-emerald-50,
                .bg-emerald-50/50 {
                    background-color: var(--brand-color)15 !important;
                }
                .text-indigo-600,
                .text-indigo-700 {
                    color: var(--brand-color) !important;
                }
                /* Focus rings and other accent buttons */
                .focus\:ring-emerald-500:focus {
                    --tw-ring-color: var(--brand-color) !important;
                }
            `}</style>
            <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
            <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
                <OfflineIndicator />
                <Header setSidebarOpen={setSidebarOpen} fetchDashboardData={fetchDashboardData} loading={loading} user={user} logout={logout} openChat={openChat} zoom={zoom} setZoom={setZoom} />
                <main className={`flex-1 ${location.pathname === '/vendor/pos' ? 'overflow-hidden' : 'overflow-y-auto custom-scrollbar'}`}>
                    <div className="w-full h-full flex flex-col">
                        <Outlet />
                    </div>
                </main>
            </div>
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30 lg:hidden" />
                )}
            </AnimatePresence>
        </div>
    );
};

export default VendorLayout;
